/**
 * A4 — Facturación → Pendientes: lo que ya toca facturar y lo que viene (este mes y el
 * siguiente), más la previsión mes a mes frente a lo facturado y cobrado.
 */
import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { ArrowLeft, Hourglass, Loader2 } from 'lucide-react'
import { api } from '../lib/api'
import { formatCents } from '../lib/money'
import PendingBillingList from '../components/billing/PendingBillingList'
import type { BillingForecastResponse, PendingBillingResponse } from '@toga/shared'

const monthLabel = (key: string) => {
  const [y, m] = key.split('-').map(Number) as [number, number]
  return new Date(y, m - 1, 1).toLocaleDateString('es-ES', { month: 'short', year: '2-digit' })
}

export default function PendingBillingPage() {
  const [totals, setTotals] = useState<PendingBillingResponse | null>(null)
  const [forecast, setForecast] = useState<BillingForecastResponse | null>(null)
  const [forecastError, setForecastError] = useState<string | null>(null)

  useEffect(() => {
    api
      .get<BillingForecastResponse>('/billing/forecast?months=12')
      .then(setForecast)
      .catch((err) => setForecastError(err instanceof Error ? err.message : 'Error'))
  }, [])

  const maxBar = Math.max(
    1,
    ...(forecast?.months.map((m) => Math.max(m.projectedCents, m.emittedCents)) ?? [0])
  )

  return (
    <div className="space-y-5 animate-slideUp">
      <header className="space-y-2">
        <Link
          to="/billing"
          className="t-muted text-sm hover:underline inline-flex items-center gap-1.5"
        >
          <ArrowLeft size={14} /> Facturación
        </Link>
        <h1 className="text-xl font-serif font-semibold t-primary flex items-center gap-2">
          <Hourglass size={20} className="text-toga-300" />
          Pendientes de facturar
        </h1>
        <p className="t-muted text-sm max-w-3xl">
          Lo que toca facturar según tus igualas, hitos de pago y presupuestos aceptados. No se crea
          ninguna factura sola: «Facturar» prepara un borrador con el emisor que corresponde, para
          que lo revises antes de emitir.
        </p>
      </header>

      {totals && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          {[
            [
              'Toca facturar ya',
              String(totals.dueCount),
              totals.dueCount > 0 ? 'text-amber-300' : 't-primary',
            ],
            ['Base de lo vencido', formatCents(totals.dueBaseCents), 't-primary'],
            ['Vence este mes y el siguiente', String(totals.upcomingCount), 't-primary'],
            ['Base de lo próximo', formatCents(totals.upcomingBaseCents), 't-primary'],
          ].map(([label, value, cls]) => (
            <div key={label} className="surface p-4">
              <div className="text-[11px] uppercase tracking-wider t-faint">{label}</div>
              <div className={`text-xl font-semibold font-mono mt-1 ${cls}`}>{value}</div>
            </div>
          ))}
        </div>
      )}

      <section className="surface overflow-hidden">
        <PendingBillingList onLoaded={setTotals} />
      </section>

      <section className="surface overflow-hidden">
        <header className="px-5 py-3.5 border-b border-base">
          <h2 className="t-primary font-semibold text-sm">Previsión de facturación (12 meses)</h2>
          <p className="t-faint text-xs mt-0.5">
            Base imponible. Lo previsto sale de los hitos, las cuotas de iguala y los presupuestos
            aceptados; lo facturado, de las facturas emitidas.
          </p>
        </header>
        {forecastError && <div className="px-5 py-4 text-xs text-rose-300">{forecastError}</div>}
        {!forecast && !forecastError && (
          <div className="px-5 py-6 t-muted text-sm flex items-center gap-2">
            <Loader2 size={14} className="animate-spin" /> Cargando…
          </div>
        )}
        {forecast && (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-[11px] uppercase tracking-wider t-faint border-b border-base">
                  <th className="px-5 py-2 font-medium">Mes</th>
                  <th className="px-3 py-2 font-medium">&nbsp;</th>
                  <th className="px-3 py-2 font-medium text-right">Previsto</th>
                  <th className="px-3 py-2 font-medium text-right">Facturado</th>
                  <th className="px-5 py-2 font-medium text-right">Cobrado</th>
                </tr>
              </thead>
              <tbody>
                {forecast.months.map((m) => (
                  <tr key={m.month} className="border-b border-base/50 last:border-b-0">
                    <td className="px-5 py-2 t-secondary capitalize">{monthLabel(m.month)}</td>
                    <td className="px-3 py-2 w-48">
                      <div className="h-2 rounded-full bg-slate-700/40 overflow-hidden">
                        <div
                          className="h-full bg-toga-500/70"
                          style={{ width: `${Math.round((m.projectedCents / maxBar) * 100)}%` }}
                        />
                      </div>
                    </td>
                    <td className="px-3 py-2 text-right font-mono t-primary">
                      {m.projectedCents ? formatCents(m.projectedCents) : '—'}
                      {m.projectedCount > 0 && (
                        <span className="t-faint text-[11px] ml-1">({m.projectedCount})</span>
                      )}
                    </td>
                    <td className="px-3 py-2 text-right font-mono t-secondary">
                      {m.emittedCents ? formatCents(m.emittedCents) : '—'}
                    </td>
                    <td className="px-5 py-2 text-right font-mono t-secondary">
                      {m.collectedCents ? formatCents(m.collectedCents) : '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr className="border-t border-base">
                  <td className="px-5 py-2 t-faint text-xs" colSpan={2}>
                    Total previsto en 12 meses
                  </td>
                  <td className="px-3 py-2 text-right font-mono t-primary font-semibold">
                    {formatCents(forecast.totalProjectedCents)}
                  </td>
                  <td colSpan={2} />
                </tr>
              </tfoot>
            </table>
          </div>
        )}
      </section>
    </div>
  )
}
